import React from 'react';
import styles from './progress-indicator.module.scss';
import classNames from 'classnames';

interface Props {
    type: 'approve' | 'billable';
    value: number;
    showWhenZero?: boolean;
    size?: number;
}

const clampValue = (value: number): number => {
    const safeValue = Number.isFinite(value) ? value : 0;
    return Math.min(Math.max(safeValue, 0), 100);
};

const VIEWBOX_SIZE = 32;
const CENTER = VIEWBOX_SIZE / 2;
const CIRCLE_RADIUS = 12;
const FULL_SECTOR_PATH = [
    `M ${CENTER} ${CENTER}`,
    `m -${CIRCLE_RADIUS} 0`,
    `a ${CIRCLE_RADIUS} ${CIRCLE_RADIUS} 0 1 0 ${CIRCLE_RADIUS * 2} 0`,
    `a ${CIRCLE_RADIUS} ${CIRCLE_RADIUS} 0 1 0 -${CIRCLE_RADIUS * 2} 0`,
    'Z'
].join(' ');

const polarToCartesian = (radius: number, angleInDegrees: number) => {
    const angleInRadians = (angleInDegrees * Math.PI) / 180;
    return {
        x: CENTER + radius * Math.cos(angleInRadians),
        y: CENTER + radius * Math.sin(angleInRadians)
    };
};

const getSectorPath = (fraction: number): string => {
    if (fraction <= 0) {
        return '';
    }

    if (fraction >= 1) {
        return FULL_SECTOR_PATH;
    }

    const startAngle = -90;
    const sweepAngle = fraction * 360;
    const endAngle = startAngle + sweepAngle;

    const start = polarToCartesian(CIRCLE_RADIUS, startAngle);
    const end = polarToCartesian(CIRCLE_RADIUS, endAngle);
    const largeArcFlag = sweepAngle > 180 ? 1 : 0;

    return [
        `M ${CENTER} ${CENTER}`,
        `L ${start.x} ${start.y}`,
        `A ${CIRCLE_RADIUS} ${CIRCLE_RADIUS} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`,
        'Z'
    ].join(' ');
};

const ProgressIndicator: React.FC<Props> = ({type, value, showWhenZero, size}) => {
    const normalizedValue = clampValue(value);

    let displayFraction = normalizedValue / 100;

    if (displayFraction < 1 && displayFraction > 0.97) {
        displayFraction = 0.97;
    } else if (displayFraction > 0 && displayFraction < 0.03) {
        displayFraction = 0.03;
    } 
    const hasProgress = displayFraction > 0;
    const isVisible = hasProgress || showWhenZero === true;

    return (
        <div
            className={classNames({
                [styles.progress]: true,
                [styles.approve]: type === 'approve',
                [styles.billable]: type === 'billable',
                [styles.active]: isVisible
            })}
            style={size ? { width: `${size}px`, height: `${size}px` } : undefined}
            aria-label={`${Math.round(normalizedValue)}%`}
        >
            <svg className={styles.circle} viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`} role="img">
                <circle className={styles.track} cx={CENTER} cy={CENTER} r={CIRCLE_RADIUS} />
                {hasProgress && <path className={styles.sector} d={getSectorPath(displayFraction)} />}
            </svg>
        </div>
    );
};

export default ProgressIndicator;
